import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { COMPLIANCE_AREAS } from "../site/content";

export default function Compliance() {
  return (
    <SiteShell>
      <PageHero
        overline="Compliance"
        title="Good contractors shouldn't miss good opportunities because of compliance gaps."
        kicker="Many local and specialist contractors have the skills, workforce and experience to deliver larger projects, but don't yet have the compliance infrastructure expected by larger organisations. Verified Workforce helps contractors identify those gaps and build the systems required to become contract-ready."
        image="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/readiness" className="vw-btn vw-btn--primary" data-testid="compliance-readiness-cta">
            Start Your Readiness Assessment
            <ArrowUpRight size={16} />
          </Link>
          <a href="#citation" className="vw-btn vw-btn--gold" data-testid="compliance-citation-cta">
            Get Compliance Support
          </a>
        </div>
      </PageHero>

      {/* Areas */}
      <section className="vw-section bg-[var(--vw-paper)]" data-testid="compliance-areas-section">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline mb-4">What We Support</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05] max-w-3xl">
              Compliance areas we help contractors understand and build.
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {COMPLIANCE_AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.04}>
                <div className="vw-card h-full" data-testid={`compliance-card-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-blue)]">
                    Area 0{i + 1}
                  </div>
                  <div className="mt-4 font-display text-lg font-semibold text-[var(--vw-navy)]">
                    {a.title}
                  </div>
                  <p className="mt-3 text-sm text-[var(--vw-muted)] leading-relaxed">
                    {a.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 text-xs text-[var(--vw-muted)] max-w-3xl">
            Verified Workforce does not award SSIP, ISO or any other external accreditation.
            We facilitate, support and connect contractors with appropriate specialist
            partners. Any external accreditation remains subject to the awarding body&apos;s
            own eligibility, assessment, terms and fees.
          </p>
        </div>
      </section>

      {/* Citation partnership */}
      <section
        id="citation"
        className="vw-section bg-[var(--vw-navy)] text-white relative overflow-hidden"
        data-testid="citation-section"
      >
        <div className="vw-grid-bg" />
        <div className="vw-container relative grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="vw-overline vw-overline--gold mb-4">Specialist Compliance Partnership</div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                Compliance support, backed by specialists.
              </h2>
              <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
                Verified Workforce works alongside Citation to help construction businesses
                access specialist Health &amp; Safety and compliance support.
              </p>
              <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
                Through our relationship with Citation, suitable contractors can access
                support covering areas including Health &amp; Safety documentation, risk
                assessments, method statements, SSIP pathways and wider compliance
                requirements. Our role is to help identify where a contractor needs to
                improve and connect them with the appropriate support to get there.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="vw-btn vw-btn--primary" data-testid="citation-support-cta">
                  Get Compliance Support
                  <ArrowUpRight size={16} />
                </Link>
                <Link to="/readiness" className="vw-btn vw-btn--gold" data-testid="citation-readiness-cta">
                  Start Your Readiness Assessment
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="relative bg-white/[0.04] border border-white/10 p-8">
                <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)] uppercase">
                  Working With
                </div>
                <div className="mt-2 font-display text-3xl font-semibold text-white">
                  Citation
                </div>
                <div className="mt-6 h-px bg-white/10" />
                <ul className="mt-6 space-y-3 text-sm text-white/80">
                  {[
                    "Health & Safety documentation",
                    "Risk assessments & method statements",
                    "SSIP pathways",
                    "Policies, procedures & handbooks",
                    "Specialist H&S advice",
                  ].map((x) => (
                    <li key={x} className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[var(--vw-gold)] mt-0.5 flex-shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-white/50 leading-relaxed">
                  Services and any accreditations provided by third parties remain subject
                  to the third party&apos;s own eligibility, assessment, terms and fees.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Portal preview (Phase 2 scaffolded) */}
      <section className="vw-section bg-white" data-testid="compliance-portal-section">
        <div className="vw-container grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="vw-overline mb-4">Coming Soon</div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                A single compliance passport for every Delivery Partner.
              </h2>
              <p className="mt-6 text-[var(--vw-muted)] leading-relaxed">
                We are developing a Verified Workforce Passport — a central contractor
                profile covering company details, trades, coverage, package capability,
                compliance documents and expiry dates, references and project history,
                network status and performance.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-[var(--vw-charcoal)]">
                {["Upload PL/EL/PI, SSIP, ISO and training certificates","Automated expiry reminders","Live status: current, expiring soon, expired, review","Share approved profile data with clients"].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)]"/>{x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <PortalMock />
            </Reveal>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function PortalMock() {
  const docs = [
    { name: "Public Liability £10m", status: "current", exp: "2026-04-12" },
    { name: "Employers' Liability £10m", status: "current", exp: "2026-04-12" },
    { name: "SSIP (CHAS Advanced)", status: "expiring_soon", exp: "2026-01-30" },
    { name: "ISO 9001:2015", status: "current", exp: "2027-06-18" },
    { name: "Working at Height Cert.", status: "expired", exp: "2025-11-02" },
    { name: "Capability Statement", status: "review_required", exp: "—" },
  ];
  const map = {
    current: ["vw-badge--blue", "Current"],
    expiring_soon: ["vw-badge--amber", "Expiring Soon"],
    expired: ["vw-badge--navy", "Expired"],
    review_required: ["vw-badge--gold", "Review"],
  };
  return (
    <div className="bg-[var(--vw-navy)] text-white p-6 lg:p-8 border border-white/10 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)]">Verified Workforce Passport</div>
          <div className="font-display text-2xl mt-1">Riverside Specialist Trades Ltd</div>
        </div>
        <div className="vw-badge vw-badge--gold">Verified</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-[11px] font-mono tracking-[0.18em] uppercase text-white/60 pb-3 border-b border-white/10">
        <div>Document</div>
        <div>Expiry</div>
        <div>Status</div>
      </div>
      {docs.map((d) => {
        const [cls, label] = map[d.status];
        return (
          <div key={d.name} className="grid grid-cols-3 gap-3 py-3 border-b border-white/5 text-sm">
            <div className="truncate">{d.name}</div>
            <div className="text-white/60 font-mono text-xs">{d.exp}</div>
            <div><span className={`vw-badge ${cls}`}>{label}</span></div>
          </div>
        );
      })}
      <p className="mt-5 text-[11px] text-white/40">Preview of the forthcoming contractor portal. Phase 2 development.</p>
    </div>
  );
}
